import { type RefObject, useId, useRef, useState } from 'react';
import { AdornedInput, Box, Button, Checkbox, FieldSet, Legend, Text } from '@zillow/constellation-10';
import { useTranslations } from 'next-intl';
import { MobileAutocompleteModal } from '@/src/components/ui/MobileAutocomplete/MobileAutocompleteModal';
import { locationPillInput } from '@/src/components/ListingSearch/locationPill.styles';
import { LucideIcon } from '@/src/components/LucideIcon';
import { POI_ICON_GROUP_ICONS } from './poiCategoryIcons';
import { POI_OPTION_GROUPS, type POIOptionValue } from './types';

type Props = {
  selectedValues: Array<POIOptionValue>;
  onChange: (values: Array<POIOptionValue>) => void;
  onClose: () => void;
  returnFocusRef: RefObject<HTMLInputElement>;
};

export function PoiSearchModal({ selectedValues, onChange, onClose, returnFocusRef }: Props) {
  const t = useTranslations('hdp');
  const common = useTranslations('common');
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();
  const groups = POI_OPTION_GROUPS.map((group) => ({
    ...group,
    options: group.options
      .map((option) => ({
        ...option,
        label: option.labelKey ? t(option.labelKey) : option.label,
      }))
      .filter((option) => option.label.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())),
  })).filter((group) => group.options.length > 0);

  return (
    <MobileAutocompleteModal
      title={t('pointsOfInterest')}
      inputRef={inputRef}
      returnFocusRef={returnFocusRef}
      onClose={onClose}
      input={
        <AdornedInput.Input
          ref={inputRef}
          css={locationPillInput}
          id={`${id}-search`}
          type="search"
          autoComplete="off"
          aria-label={t('poiSearchPlaceholder')}
          placeholder={t('poiSearchPlaceholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.preventDefault();
          }}
        />
      }
      footer={
        <Button type="button" tone="brand" emphasis="filled" onClick={onClose} css={{ width: '100%' }}>
          {t('poiSearchDone')}
        </Button>
      }
    >
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default', padding: 'default' }}>
        {groups.map((group) => (
          <FieldSet key={group.labelKey} legend={<Legend>{t(group.labelKey)}</Legend>}>
            {group.options.map((option) => (
              <Box
                asChild
                key={option.value}
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'tighter',
                  minHeight: 'tapArea.mobile.default',
                  cursor: 'pointer',
                }}
              >
                <label htmlFor={`${id}-${option.value}`}>
                  <Checkbox
                    id={`${id}-${option.value}`}
                    checked={selectedValues.includes(option.value)}
                    onChange={(event) =>
                      onChange(
                        event.target.checked
                          ? [...selectedValues, option.value]
                          : selectedValues.filter((value) => value !== option.value),
                      )
                    }
                  />
                  <LucideIcon icon={POI_ICON_GROUP_ICONS[option.iconGroup]} size="sm" />
                  <Text asChild>
                    <span>{option.label}</span>
                  </Text>
                </label>
              </Box>
            ))}
          </FieldSet>
        ))}
        {!groups.length ? <Text role="status">{common('noResults')}</Text> : null}
      </Box>
    </MobileAutocompleteModal>
  );
}
